import { createWaveChartTheme, MUI_CHART_COLORS } from '../../../../lib/scichart/themes'

export type ChartTheme = 'dark' | 'light'

export interface ChartColors {
  background: string
  border: string
  text: {
    primary: string
    secondary: string
  }
  empty: {
    background: string
    text: string
  }
  dragOverlay: {
    background: string
    chipBackground: string
    chipText: string
    chipBorder: string
  }
  closeButton: {
    background: string
    hover: string
    text: string
    border: string
  }
}

export interface ChartStyleOptions {
  borderRadius: string
  fontSize: {
    small: string
    medium: string
    large: string
  }
  spacing: {
    small: number
    medium: number
    large: number
  }
  shadows: {
    light: string
    medium: string
    heavy: string
  }
}

/**
 * Manages chart theming, colors, and styling across the chart system
 * Provides consistent theme application and dynamic theme switching
 */
export class ChartThemeManager {
  private static readonly CHART_COLOR_PALETTE = [
    '#1976d2', '#dc004e', '#9c27b0', '#673ab7', '#3f51b5',
    '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
    '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800',
    '#ff5722', '#795548', '#9e9e9e', '#607d8b', '#e91e63'
  ]

  /**
   * Gets theme-specific colors for chart components
   */
  static getChartColors(theme: ChartTheme): ChartColors {
    if (theme === 'dark') {
      return {
        background: '#0a0a0a',
        border: 'rgba(255, 255, 255, 0.12)',
        text: {
          primary: 'rgba(255, 255, 255, 0.87)',
          secondary: 'rgba(255, 255, 255, 0.7)'
        },
        empty: {
          background: '#0a0a0a',
          text: '#666'
        },
        dragOverlay: {
          background: 'rgba(0, 0, 0, 0.3)',
          chipBackground: '#424242',
          chipText: '#ffffff',
          chipBorder: '#616161'
        },
        closeButton: {
          background: 'transparent',
          hover: 'rgba(244, 67, 54, 0.8)',
          text: 'rgba(255, 255, 255, 0.7)',
          border: 'rgba(255, 255, 255, 0.12)'
        }
      }
    }

    // Light theme
    return {
      background: '#f9f9f9',
      border: 'rgba(0, 0, 0, 0.12)',
      text: {
        primary: 'rgba(0, 0, 0, 0.87)',
        secondary: '#424242'
      },
      empty: {
        background: '#f9f9f9',
        text: '#999'
      },
      dragOverlay: {
        background: 'rgba(0, 0, 0, 0.3)',
        chipBackground: '#e0e0e0',
        chipText: '#424242',
        chipBorder: '#bdbdbd'
      },
      closeButton: {
        background: 'transparent',
        hover: 'rgba(244, 67, 54, 0.8)',
        text: 'rgba(0, 0, 0, 0.7)',
        border: 'rgba(0, 0, 0, 0.12)'
      }
    }
  }

  /**
   * Gets styling options for chart components
   */
  static getChartStyles(): ChartStyleOptions {
    return {
      borderRadius: '4px',
      fontSize: {
        small: '12px',
        medium: '14px',
        large: '16px'
      },
      spacing: {
        small: 8,
        medium: 16,
        large: 24
      },
      shadows: {
        light: '0 1px 3px rgba(0, 0, 0, 0.12)',
        medium: '0 4px 6px rgba(0, 0, 0, 0.16)',
        heavy: '0 10px 25px rgba(0, 0, 0, 0.19)'
      }
    }
  }

  /**
   * Creates SciChart theme configuration for the specified theme
   */
  static createSciChartTheme(theme: ChartTheme) {
    return createWaveChartTheme(theme)
  }

  /**
   * Gets a color from the chart color palette by index
   */
  static getChartColor(index: number): string {
    return this.CHART_COLOR_PALETTE[index % this.CHART_COLOR_PALETTE.length]
  }

  /**
   * Gets all available chart colors
   */
  static getChartColorPalette(): string[] {
    return [...this.CHART_COLOR_PALETTE]
  }

  /**
   * Gets MUI-compatible chart colors
   */
  static getMuiChartColors(): string[] {
    return [...MUI_CHART_COLORS]
  }

  /**
   * Generates a unique color for a curve based on its name and context
   */
  static generateCurveColor(curveName: string, chartId: string, curveIndex: number): string {
    // Use curve index as primary source for consistency
    if (curveIndex >= 0) {
      return this.getChartColor(curveIndex)
    }

    // Fallback to hash-based color generation for consistent colors
    const hash = this.hashString(`${chartId}-${curveName}`)
    return this.getChartColor(hash)
  }

  /**
   * Creates CSS-in-JS styles for empty chart placeholder
   */
  static createEmptyChartStyles(theme: ChartTheme): React.CSSProperties {
    const colors = this.getChartColors(theme)
    const styles = this.getChartStyles()

    return {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: styles.spacing.medium,
      backgroundColor: colors.empty.background,
      color: colors.empty.text,
      fontSize: styles.fontSize.medium,
      textAlign: 'center',
      padding: styles.spacing.large * 2,
      borderRadius: styles.borderRadius
    }
  }

  /**
   * Creates CSS-in-JS styles for drag overlay
   */
  static createDragOverlayStyles(theme: ChartTheme): React.CSSProperties {
    const colors = this.getChartColors(theme)

    return {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colors.dragOverlay.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
      zIndex: 10
    }
  }

  /**
   * Creates CSS-in-JS styles for chart close button
   */
  static createCloseButtonStyles(theme: ChartTheme): React.CSSProperties {
    const colors = this.getChartColors(theme)
    const styles = this.getChartStyles()

    return {
      position: 'absolute',
      top: styles.spacing.small,
      right: styles.spacing.small,
      width: 24,
      height: 24,
      border: '1px solid',
      borderColor: colors.closeButton.border,
      borderRadius: styles.borderRadius,
      backgroundColor: colors.closeButton.background,
      color: colors.closeButton.text,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: styles.fontSize.medium,
      fontWeight: '400',
      lineHeight: '1',
      transition: 'all 0.2s ease'
    }
  }

  /**
   * Creates hover styles for close button
   */
  static createCloseButtonHoverStyles(theme: ChartTheme): React.CSSProperties {
    const colors = this.getChartColors(theme)

    return {
      backgroundColor: colors.closeButton.hover,
      color: 'white',
      borderColor: colors.closeButton.hover
    }
  }

  /**
   * Validates if a theme is supported
   */
  static isValidTheme(theme: string): theme is ChartTheme {
    return theme === 'dark' || theme === 'light'
  }

  /**
   * Gets the opposite theme (for toggle functionality)
   */
  static getOppositeTheme(theme: ChartTheme): ChartTheme {
    return theme === 'dark' ? 'light' : 'dark'
  }

  /**
   * Simple hash function for consistent color generation
   */
  private static hashString(str: string): number {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return Math.abs(hash)
  }

  /**
   * Creates theme-aware Chart component styles
   */
  static createChartContainerStyles(theme: ChartTheme): React.CSSProperties {
    const colors = this.getChartColors(theme)
    const styles = this.getChartStyles()

    return {
      position: 'relative',
      border: '1px solid',
      borderColor: colors.border,
      borderRadius: styles.borderRadius,
      backgroundColor: colors.background,
      overflow: 'hidden'
    }
  }

  /**
   * Creates theme-aware header styles
   */
  static createChartHeaderStyles(theme: ChartTheme): React.CSSProperties {
    const colors = this.getChartColors(theme)
    const styles = this.getChartStyles()

    return {
      padding: `${styles.spacing.small}px ${styles.spacing.medium}px`,
      borderBottom: '1px solid',
      borderBottomColor: colors.border,
      backgroundColor: colors.background,
      color: colors.text.primary,
      fontSize: styles.fontSize.medium,
      fontWeight: '500'
    }
  }
} 